import { Tree } from "fluid-framework";
import { useCallback, useSyncExternalStore } from "react";

import { getUser } from "../fluid/fluid.service.js";
import { useFluid } from "../fluid/hooks/use-fluid.js";

/**
 * Reactive source of truth for whether the current user is currently in the
 * office.
 *
 * The backend inactivity cleanup sets the Fluid `isOnline` flag to `false` when
 * it kicks a user (see `markUserAsInactive` in the office schema), and rejoining
 * sets it back to `true`. Reading this flag lets us react to a kick directly
 * from shared state instead of inferring it from UI side effects such as an open
 * modal.
 */
export const useAmIInOffice = (): boolean => {
  const { fluidUserId, sharedTree } = useFluid();

  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      Tree.on(sharedTree.root.users, "treeChanged", onStoreChange),
    [sharedTree]
  );

  const getSnapshot = useCallback(
    () => getUser({ sharedTree, userId: fluidUserId })?.isOnline ?? false,
    [fluidUserId, sharedTree]
  );

  return useSyncExternalStore(subscribe, getSnapshot);
};
